"use client";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Blog.module.css";
// import AxiosInstance from "../../services/Api";
import AxiosInstance, { IMAGE_BASE_URL } from "../../services/Api";
import seoConfig from "../../seo/seoConfig";
import SEO from "../../seo/SEO";

const PAGE_SIZE = 6;
const fallbackCardImage =
  "https://lh3.googleusercontent.com/aida-public/AB6AXuC9HQUCpha79m5ojhdmprH7HIa-bp2j9xJONxY55sGxE4tq4jd1iDo8tL1hI__gTulHRcltbIgT_L966JRYjvMLZOugqfgHgJ9BJx_jPjM5QRGRIrVwFAPKuJMtrwow5dWGE3lnwiVWaALPzWmjdxfZKDtzwXnrfxGnurPcJlh4Sguad0GCm-USXmsgI-rEvq_V5Q-NSfDCayqYRUkCZNIB1dCkfrVjr6K1PZO6qQ_WpsOhU2Ep6nfFJtUdhEz-je6dBI2e0FkzyaVm";

// const toFullImageUrl = (path) => {
//   if (!path) return "";
//   const rawPath = String(path).trim();
//   if (
//     rawPath.startsWith("http://") ||
//     rawPath.startsWith("https://")
//   ) {
//     return rawPath;
//   }
//   // Old local blob URLs from API cannot be resolved on other sessions/devices.
//   if (rawPath.startsWith("blob:")) return "";

//   const apiBaseUrl =
//     AxiosInstance?.defaults?.baseURL ||
//     process.env.REACT_APP_API_URL ||
//     "http://192.168.1.16:8087/V1/api";
//   const base = apiBaseUrl.endsWith("/") ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
//   const origin = (() => {
//     try {
//       return new URL(base).origin;
//     } catch {
//       return base;
//     }
//   })();

//   // /public/... should use domain root (without /api prefix)
//   if (rawPath.startsWith("/public/")) {
//     return `${origin}${rawPath}`;
//   }

//   // Relative file paths use API base URL
//   return `${base}/${rawPath.replace(/^\/+/, "")}`;
// };

const toFullImageUrl = (path) => {
  if (!path) return "";
  const rawPath = String(path).trim();

  // Already a full URL — use as-is
  if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) {
    return rawPath;
  }

  // Blob URLs from previous sessions won't work
  if (rawPath.startsWith("blob:")) return "";

  // ✅ Extract origin from IMAGE_BASE_URL so we don't double-prefix `/api/`
  // Local:  IMAGE_BASE_URL = "http://localhost:8087"        → origin = "http://localhost:8087"
  // Dev:    IMAGE_BASE_URL = "https://dev.ifillip.com/api/" → origin = "https://dev.ifillip.com"
  const origin = (() => {
    try {
      return new URL(IMAGE_BASE_URL).origin;
    } catch {
      // Fallback: strip trailing slash + `/api`
      return IMAGE_BASE_URL.replace(/\/+$/, "").replace(/\/api$/, "");
    }
  })();

  // rawPath already includes `/api/...` (e.g. "/api/uploads/blog/blog_xxx.jpg")
  const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

  return `${origin}${cleanPath}`;
};

const toCategoryClass = (categoryName = "") => {
  const value = String(categoryName).toLowerCase();
  if (value.includes("design")) return "design";
  if (value.includes("engineer") || value.includes("tech"))
    return "engineering";
  return "product";
};

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

export default function Blog() {
  const [activePage, setActivePage] = useState(1);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await AxiosInstance.post("/blog/list", {
          blog_status: 1,
          tag_id: "",
          author_name: "",
          author_designation: "",
          categories_id: "",
          start_date: "",
          end_date: "",
          search: "",
        });

        if (response?.data?.status && Array.isArray(response?.data?.blog)) {
          setBlogs(response.data.blog);
        } else {
          setBlogs([]);
          setError(response?.data?.message || "Failed to fetch blogs.");
        }
      } catch (err) {
        setBlogs([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch blogs.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const navigate = useNavigate();

  // const handleReadMore = (post) => {
  //   const livePath = String(post?.live_file_path || "").trim();
  //   if (livePath) {
  //     if (livePath.startsWith("http://") || livePath.startsWith("https://")) {
  //       window.location.href = livePath;
  //       return;
  //     }
  //     const normalizedPath = livePath.startsWith("/") ? livePath : `/${livePath}`;
  //     navigate(normalizedPath);
  //     return;
  //   }
  //   navigate(`/blogView?id=${post?.id}`);
  // };

  const handleReadMore = (post) => {
    const livePath = String(post?.live_file_path || "").trim();

    let url;

    if (livePath && livePath !== "0") {
      if (livePath.startsWith("http://") || livePath.startsWith("https://")) {
        url = livePath;
      } else {
        url = livePath.startsWith("/") ? livePath : `/${livePath}`;
      }
    } else {
      url = `/blogView?id=${post?.id}`;
    }

    // Open in new tab with hard refresh (noopener for security)
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalPages = Math.max(1, Math.ceil(blogs.length / PAGE_SIZE));

  const blogPosts = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return blogs.slice(start, start + PAGE_SIZE);
  }, [blogs, activePage]);

  const popularTags = useMemo(() => {
    const names = blogs
      .flatMap((blog) =>
        Array.isArray(blog?.tag) ? blog.tag.map((t) => t?.tag_name) : [],
      )
      .filter(Boolean);
    return Array.from(new Set(names)).slice(0, 8);
  }, [blogs]);

  const trendingPosts = useMemo(
    () =>
      [...blogs]
        .sort(
          (a, b) =>
            new Date(b?.posted_date || 0).getTime() -
            new Date(a?.posted_date || 0).getTime(),
        )
        .slice(0, 3)
        .map((post, index) => ({
          id: post?.id,
          live_file_path: post?.live_file_path, // ← add this
          number: String(index + 1).padStart(2, "0"),
          title: post?.title || "Untitled",
          meta: `${formatDate(post?.posted_date) || "Recent"}${
            post?.author_name ? ` • ${post.author_name}` : ""
          }`,
        })),
    [blogs],
  );

  useEffect(() => {
    if (activePage > totalPages) {
      setActivePage(totalPages);
    }
  }, [activePage, totalPages]);

  return (
    <>
      <SEO {...seoConfig.blog} />
      <div className={styles.blogListPage}>
        <main>
          {/* ── HERO HEADER ──────────────────────────────────── */}
          <header className={styles.heroHeader}>
            <div className="container">
              <div className={styles.heroContent}>
                <h1 className={styles.heroTitle}>
                  Insights &amp; Perspectives
                </h1>
                <p className={styles.heroSubtitle}>
                  Deep dives into the engineering, design, and product
                  strategies powering the next generation of software
                  excellence.
                </p>
              </div>
            </div>
          </header>

          {/* ── MAIN LAYOUT (Cards + Sidebar) ─────────────── */}
          <div className="container">
            <div className="row g-5">
              {/* ── BLOG GRID ─────────────────────── */}
              <div className="col-lg-8">
                {error ? <p className={styles.heroSubtitle}>{error}</p> : null}
                <div className="row g-4">
                  {loading ? (
                    <p className={styles.heroSubtitle}>Loading blogs...</p>
                  ) : null}
                  {blogPosts.map((post) => (
                    <div key={post.id} className="col-md-6">
                      <article className={styles.blogCard}>
                        <div className={styles.cardImageWrap}>
                          <img
                            src={toFullImageUrl(post?.cover_image_path)}
                            alt={post.title}
                            className={styles.cardImage}
                            onError={(e) => {
                              e.currentTarget.src = fallbackCardImage;
                            }}
                          />
                        </div>

                        <div className={styles.cardBody}>
                          <span
                            className={`${styles.categoryBadge} ${
                              styles[
                                `badge_${toCategoryClass(post?.categorie_name)}`
                              ]
                            }`}
                          >
                            {post?.categorie_name || "General"}
                          </span>

                          <h3 className={styles.cardTitle}>{post.title}</h3>
                          <p className={styles.cardExcerpt}>{post.tagline}</p>

                          <div className={styles.cardMeta}>
                            <div className={styles.authorBlock}>
                              <div>
                                <p className={styles.authorName}>
                                  {post.author_name}
                                </p>
                                <p className={styles.authorRole}>
                                  {post.author_designation}
                                </p>
                              </div>
                            </div>
                            <div className={styles.dateBlock}>
                              <p className={styles.dateText}>
                                {formatDate(post?.posted_date)}
                              </p>
                            </div>
                          </div>

                          <button
                            className={styles.readMoreBtn}
                            onClick={() => handleReadMore(post)}
                          >
                            Read More
                          </button>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>

                {/* ── PAGINATION ─────────────────── */}
                {!loading && blogs.length > PAGE_SIZE ? (
                  <div className={styles.pagination}>
                    <button
                      className={styles.pageNavBtn}
                      onClick={() => setActivePage((p) => Math.max(1, p - 1))}
                      aria-label="Previous"
                    >
                      <i className="fas fa-arrow-left" />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .slice(0, 5)
                      .map((p) => (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${
                            activePage === p ? styles.pageBtnActive : ""
                          }`}
                          onClick={() => setActivePage(p)}
                        >
                          {p}
                        </button>
                      ))}

                    <button
                      className={styles.pageNavBtn}
                      onClick={() =>
                        setActivePage((p) => Math.min(totalPages, p + 1))
                      }
                      aria-label="Next"
                    >
                      <i className="fas fa-arrow-right" />
                    </button>
                  </div>
                ) : null}
              </div>

              {/* ── SIDEBAR ──────────────────────── */}
              <aside className="col-lg-4">
                <div className={styles.stickySidebar}>
                  {/* Trending Posts */}
                  <section>
                    <h4 className={styles.sidebarHeading}>Trending Posts</h4>
                    <div className="d-flex flex-column gap-4">
                      {trendingPosts.map((post, idx) => {
                        const livePath = String(
                          post?.live_file_path || "",
                        ).trim();
                        const href =
                          livePath && livePath !== "0"
                            ? livePath.startsWith("http")
                              ? livePath
                              : `${window.location.origin}${livePath.startsWith("/") ? livePath : `/${livePath}`}`
                            : `/blogView?id=${post.id}`;

                        return (
                          <a
                            key={idx}
                            href={href}
                            className={styles.trendingItem}
                            // if livePath is an HTML file, do a hard navigation so router doesn't swallow it
                            {...(livePath && livePath !== "0"
                              ? {
                                  onClick: (e) => {
                                    e.preventDefault();
                                    window.location.href = href;
                                  },
                                }
                              : {})}
                          >
                            <span className={styles.trendingNumber}>
                              {post.number}
                            </span>
                            <div>
                              <h5 className={styles.trendingTitle}>
                                {post.title}
                              </h5>
                              <p className={styles.trendingMeta}>{post.meta}</p>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </section>

                  {/* Contact Card */}
                  <section className={styles.newsletterCard}>
                    <div className={styles.newsletterContent}>
                      <h4 className={styles.newsletterTitle}>Contact us</h4>
                      <p className={styles.newsletterText}>
                        Have questions about our platform, pricing, or support?
                        Our team is ready to help.
                      </p>
                      <a href="/contact" className={styles.newsletterBtn}>
                        Go to Contact Page
                      </a>
                    </div>
                    <div className={styles.newsletterDecor} />
                  </section>

                  {/* Popular Tags */}
                  <section>
                    <h4 className={styles.sidebarHeading}>Popular Tags</h4>
                    <div className="d-flex flex-wrap gap-2">
                      {popularTags.map((tag, idx) => (
                        <a key={idx} href="#" className={styles.tagPill}>
                          {tag}
                        </a>
                      ))}
                    </div>
                  </section>
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
